import { useLayoutEffect, useRef, useState } from "react";

const NUMBER = /-?\d[\d,]*(\.\d+)?/;

const format = (value: number, decimals: number, grouped: boolean) =>
  grouped
    ? value.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : value.toFixed(decimals);

/**
 * Animates the first number inside `text` up to its value, keeping whatever
 * surrounds it ("USD 1,250.00", "42 bookings"). Text without a number
 * ("Loading...", "Hidden") is returned as-is, and the next number counts up
 * from the last one shown, so revealing a hidden figure animates too.
 */
export function useCountUpText(text: string, duration = 700): string {
  const [display, setDisplay] = useState(text);
  const lastValue = useRef(0);

  useLayoutEffect(() => {
    const match = text.match(NUMBER);
    if (!match || match.index === undefined) {
      setDisplay(text);
      return;
    }
    const target = parseFloat(match[0].replace(/,/g, ""));
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const from = lastValue.current;
    if (!Number.isFinite(target) || reduceMotion || from === target) {
      lastValue.current = target;
      setDisplay(text);
      return;
    }

    const decimals = match[1] ? match[1].length - 1 : 0;
    const grouped = match[0].includes(",");
    const prefix = text.slice(0, match.index);
    const suffix = text.slice(match.index + match[0].length);
    let current = from;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      current = from + (target - from) * eased;
      if (t < 1) {
        setDisplay(prefix + format(current, decimals, grouped) + suffix);
        frame = requestAnimationFrame(tick);
      } else {
        lastValue.current = target;
        setDisplay(text);
      }
    };
    setDisplay(prefix + format(from, decimals, grouped) + suffix);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      lastValue.current = current;
    };
  }, [text, duration]);

  return display;
}
